import { Fragment, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useDrop, useDragLayer } from 'react-dnd';
import { Box, Stack, SxProps, Typography } from '@mui/material';
import { isEmptyId, DragAndDropIllustration } from '@diana/core';
import {
  FieldTypeComponent,
  ContainerTypeComponent,
  IDndLayoutComponentDragObject as IDragObject,
  IDndLayoutComponentDropResult as IDropResult,
  IDndLayoutComponentLocation,
  IDndLayoutComponentDropResult,
} from '../../../../../providers/DndLayoutComponents2';
import { FLOW_BUILDER2_PAGE_NS } from '../../../i18n';
import { useDndComponentHoverEvent } from '../ComponentBuilder/useDndComponentHoverEvent';
import { useDndComponentDropEvent } from '../ComponentBuilder/useDndComponentDropEvent';
import { usePrepareComponentToAddInLayout } from '../../../Support/usePrepareComponentToAddInLayout';

export interface IDndDropAreaProps {
  index: number;
  parentId: string;
  layoutId: string;
  taskId: string | null;
  stepId: string | null;
  flowId: string | null;
  sx?: SxProps;
}

export const DndDropArea = (props: IDndDropAreaProps) => {
  const { index, parentId, layoutId, taskId, stepId, flowId, sx = {} } = props;

  const { t } = useTranslation(FLOW_BUILDER2_PAGE_NS);

  const bodyRef = useRef<HTMLElement>(null);

  const disabled = useDragLayer<boolean, IDragObject>((monitor) => {
    const itemType = monitor.getItemType();
    if (itemType === ContainerTypeComponent) return !isEmptyId(parentId);
    return false;
  });

  const { onDndComponentHover } = useDndComponentHoverEvent({ layoutId, taskId, stepId, flowId });
  const { onDndComponentDrop } = useDndComponentDropEvent({ layoutId, taskId, stepId, flowId });

  const prepareComponentToAddInLayout = usePrepareComponentToAddInLayout({
    parentId,
    layoutId,
    taskId,
    stepId,
    flowId,
  });

  const location = useMemo<IDndLayoutComponentLocation>(() => {
    return { layoutId, parentId, index };
  }, [index, layoutId, parentId]);

  const dropResult = useMemo<IDndLayoutComponentDropResult>(() => {
    return { componentId: null, componentSize: 12, location };
  }, [location]);

  const [{ isOver }, drop] = useDrop<IDragObject, IDropResult, { isOver: boolean }>(
    {
      accept: [FieldTypeComponent, ContainerTypeComponent],
      collect: (monitor) => ({ isOver: monitor.isOver() }),
      drop: (dragItem, monitor) => {
        if (disabled) return;

        const components = prepareComponentToAddInLayout({ ...dragItem.component, parentId });

        const dragObject: typeof dragItem = {
          ...dragItem,
          component: components[0],
          descendants: [...components.slice(1), ...dragItem.descendants],
        };

        onDndComponentHover({
          dragObject,
          monitor,
          targetElement: null,
          targetDropResult: dropResult,
        });

        return onDndComponentDrop({
          dragObject,
          monitor,
          targetElement: null,
          targetDropResult: dropResult,
        });
      },
    },
    [disabled, dropResult, onDndComponentHover, onDndComponentDrop, prepareComponentToAddInLayout],
  );

  drop(bodyRef);

  return (
    <Box ref={bodyRef} sx={{ height: 315, width: '100%', position: 'relative', ...sx }}>
      <Stack
        sx={({ palette, spacing }) => ({
          width: '100%',
          height: '100%',
          alignItems: 'center',
          justifyContent: 'center',
          border: `1px dashed ${
            disabled ? 'rgba(0,0,0,0)' : isOver ? palette.primary.main : palette.border.main
          }`,
          borderRadius: spacing(0.75),
        })}
      >
        <Stack spacing={1} alignItems="center">
          {!disabled && (
            <Fragment>
              <DragAndDropIllustration />
              <Typography color="text.secondary">
                {t('descriptions.dragAndDropComponents')}
              </Typography>
            </Fragment>
          )}
        </Stack>
      </Stack>
      {isOver && !disabled && (
        <Box
          sx={({ palette, spacing }) => ({
            top: 0,
            right: 0,
            bottom: 0,
            left: 0,
            zIndex: 0,
            opacity: 0.05,
            position: 'absolute',
            borderRadius: spacing(0.75),
            backgroundColor: palette.primary.main,
          })}
        />
      )}
    </Box>
  );
};
